<div class="absolute inset-0 flex flex-col min-w-0 overflow-hidden">
    <!-- Header -->
    <div class="flex sm:flex-row flex-0 sm:items-center sm:justify-between p-6 pb-22 sm:pt-12 sm:pb-28 sm:px-10 bg-default dark:bg-transparent">
        <div class="flex-1 min-w-0">
            <div class="flex">
                <a class="inline-flex items-center -ml-1.5 text-secondary font-medium" [routerLink]="'/attendance'">
                    <mat-icon class="icon-size-5 text-secondary" [svgIcon]="'heroicons_solid:chevron-left'"></mat-icon>
                    <span class="ml-1">Back to Schedules</span>
                </a>
            </div>
            <div class="mt-2">
                <h2 class="text-3xl md:text-4xl font-extrabold tracking-tight leading-7 sm:leading-10 truncate">
                    Attendance Details
                </h2>
                <div *ngIf="classSchedule" class="mt-2 text-lg">
                    {{ classSchedule.Date__c | date:'fullDate' }} •
                    {{ formatTime(classSchedule.Start_Time__c) }} - {{ formatTime(classSchedule.End_Time__c) }}
                </div>
            </div>
        </div>
    </div>

    <!-- Main Content -->
    <div class="flex-auto -mt-16 p-6 sm:p-10 rounded-t-2xl shadow overflow-y-auto bg-card" cdkScrollable>
        <!-- Permission denied message -->
        <div *ngIf="!canViewAttendance" class="w-full py-8 text-center">
            <mat-icon class="text-6xl text-gray-400 mb-4">block</mat-icon>
            <h2 class="text-2xl font-bold text-gray-600 mb-2">Access Denied</h2>
            <p class="text-gray-500">You don't have permission to view attendance details.</p>
        </div>

        <!-- Content when user has view permission -->
        <div *ngIf="canViewAttendance">
            <!-- Class Schedule Selector -->
            <div *ngIf="allClassSchedules.length > 0" class="mb-6">
            </div>

            <!-- Loading Indicator -->
            <div *ngIf="pageLoader" class="flex justify-center items-center py-12">
                <mat-spinner diameter="40"></mat-spinner>
                <span class="ml-4">{{ pageLoaderText }}</span>
            </div>

            <!-- Content when not loading -->
            <ng-container *ngIf="!pageLoader">
                <!-- Save Button (only shown when no existing attendance and has edit permission) -->
                <div *ngIf="!hasExistingAttendance && canCreateAttendance" class="flex justify-end mb-6">
                    <button mat-raised-button color="primary" 
                            (click)="onFormSubmit()" 
                            [disabled]="isSaving || registrations.length === 0"
                            class="min-w-32">
                        <span *ngIf="!isSaving">Save Attendance</span>
                        <span *ngIf="isSaving" class="flex items-center">
                            <mat-spinner diameter="20" class="mr-2"></mat-spinner>
                            Saving...
                        </span>
                    </button>
                </div>

                <!-- Edit/Save/Cancel Buttons (shown when there is existing attendance and has edit permission) -->
                <div *ngIf="hasExistingAttendance && canEditAttendance" class="flex justify-end mb-6 gap-2">
                    <button *ngIf="!isEditing" mat-raised-button color="accent" 
                            (click)="toggleEditMode()" class="min-w-32">
                        Edit Attendance
                    </button>
                    
                    <div *ngIf="isEditing" class="flex gap-2">
                        <button mat-raised-button color="warn" 
                                (click)="toggleEditMode()" class="min-w-32">
                            Cancel
                        </button>
                        <button mat-raised-button color="primary" 
                                (click)="saveEditedAttendance()" 
                                [disabled]="isSaving"
                                class="min-w-32">
                            <span *ngIf="!isSaving">Save Changes</span>
                            <span *ngIf="isSaving" class="flex items-center">
                                <mat-spinner diameter="20" class="mr-2"></mat-spinner>
                                Saving...
                            </span>
                        </button>
                    </div>
                </div>

                <!-- Attendance Table -->
                <div *ngIf="registrations.length > 0" class="overflow-x-auto">
                    <table mat-table [dataSource]="registrations" class="mat-elevation-z8 w-full">
                        <!-- Student Name Column -->
                        <ng-container matColumnDef="student">
                            <th mat-header-cell *matHeaderCellDef>Student Name</th>
                            <td mat-cell *matCellDef="let reg">
                                {{ reg.Kid_name__c || '--' }}
                            </td>
                        </ng-container>

                        <!-- Parent Email Column -->
                        <ng-container matColumnDef="email">
                            <th mat-header-cell *matHeaderCellDef>Parent Email</th>
                            <td mat-cell *matCellDef="let reg">
                                {{ reg.Parent_Email__c || '--' }}
                            </td>
                        </ng-container>

                        <!-- Payment Status Column -->
                        <ng-container matColumnDef="payment">
                            <th mat-header-cell *matHeaderCellDef>Payment Status</th>
                            <td mat-cell *matCellDef="let reg">
                                <span [class.text-green-600]="reg.paymentStatus === 'Paid'"
                                      [class.text-red-600]="reg.paymentStatus === 'Unpaid'"
                                      class="font-medium">
                                    {{ reg.paymentStatus }}
                                </span>
                            </td>
                        </ng-container>

                        <!-- Attendance Column -->
                        <ng-container matColumnDef="attendance">
                            <th mat-header-cell *matHeaderCellDef class="!text-center">Attendance</th>
                            <td mat-cell *matCellDef="let reg" class="!text-center !px-2">
                                <!-- Show read-only status when viewing existing attendance -->
                                <div *ngIf="hasExistingAttendance && !isEditing" class="flex justify-center">
                                    <span class="px-4 py-2 rounded-lg font-medium"
                                        [class.bg-green-100]="reg.attendanceStatus === 'Present'"
                                        [class.text-green-800]="reg.attendanceStatus === 'Present'"
                                        [class.bg-red-100]="reg.attendanceStatus === 'Absent'"
                                        [class.text-red-800]="reg.attendanceStatus === 'Absent'">
                                        {{ reg.attendanceStatus || '--' }}
                                    </span>
                                </div>
                                
                                <!-- Show attendance buttons when creating new or editing and has edit permission -->
                                <div *ngIf="(!hasExistingAttendance || isEditing) && canEditOrCreateAttendance" class="flex justify-center gap-2">
                                    <button
                                        mat-stroked-button
                                        class="!min-w-0 !px-3 !py-1.5 text-sm rounded-lg border transition-all duration-200 ease-in-out hover:shadow-md"
                                        [class.bg-green-500]="reg.attendanceStatus === 'Present'"
                                        [class.text-white]="reg.attendanceStatus === 'Present'"
                                        [class.border-green-200]="reg.attendanceStatus === 'Present'"
                                        (click)="setAttendance(reg, 'Present')"
                                    >
                                        ✅
                                    </button>
                                    <button
                                        mat-stroked-button
                                        class="!min-w-0 !px-3 !py-1.5 text-sm rounded-lg border transition-all duration-200 ease-in-out hover:shadow-md"
                                        [class.bg-red-400]="reg.attendanceStatus === 'Absent'"
                                        [class.text-white]="reg.attendanceStatus === 'Absent'"
                                        [class.border-red-200]="reg.attendanceStatus === 'Absent'"
                                        (click)="setAttendance(reg, 'Absent')"
                                    >
                                        ❌
                                    </button>
                                </div>
                            </td>
                        </ng-container>
                        <tr mat-header-row *matHeaderRowDef="['student', 'email', 'payment', 'attendance']"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['student', 'email', 'payment', 'attendance']"></tr>
                    </table>
                </div>

                <div *ngIf="registrations.length === 0 && !pageLoader" class="w-full py-8 text-center font-bold text-lg text-gray-600">
                    No students registered for this class.
                </div>
            </ng-container>
        </div>
    </div>
</div>